import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import { getHotMedia } from '@/utils/media';

export default function HeroCarousel() {
  const slides = getHotMedia(5);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  const touchStartX = useRef(0);
  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
  };

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[520px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={slide.cover}
            alt={`${slide.title} 焦点海报`}
            title={`${slide.title} 热门推荐`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full flex-col justify-end px-4 pb-10 md:px-6 md:pb-14">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`max-w-2xl transition-all duration-700 ${
              i === index ? 'block translate-y-0 opacity-100' : 'hidden translate-y-4 opacity-0'
            }`}
          >
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
                {slide.rating.toFixed(1)} 分
              </span>
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs text-white backdrop-blur">
                {slide.type === 'movie' ? '电影' : '热播剧'}
              </span>
              {slide.genres.slice(0, 3).map((g) => (
                <span key={g} className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur">
                  {g}
                </span>
              ))}
            </div>
            <h2 className="font-heading text-3xl font-bold text-white md:text-5xl">{slide.title}</h2>
            <p className="mt-3 line-clamp-2 max-w-xl text-sm text-white/80 md:text-base">{slide.synopsis}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg"></i> 查看详情
              </Link>
              <Link
                to={`/category/${slide.type}`}
                className="whitespace-nowrap rounded-md border border-white/40 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/10"
              >
                更多同类
              </Link>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 md:flex"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/30 text-white hover:bg-black/50 md:flex"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? 'w-6 bg-primary-500' : 'w-2 bg-white/60'
            }`}
            aria-label={`切换到第${i + 1}张`}
          ></button>
        ))}
      </div>
    </section>
  );
}